export type MapboxFeature = {
  feature_type?: string;
  name?: string;
  name_preferred?: string;
  overrideName?: string;
  place_formatted?: string;
  address?: string;
  context?: {
    country?: { name?: string; country_code?: string };
    region?: { name?: string; region_code?: string };
    district?: { name?: string };
    place?: { name?: string };
    neighborhood?: { name?: string };
    postcode?: { name?: string };
  };
};

/**
 * Format location for compact display (chips, recent searches, rate-watch headline)
 * Returns a concise, context-aware string based on feature_type
 */
export const formatPlaceShort = (feature: MapboxFeature | undefined): string | undefined => {
  if (!feature) return undefined;

  if (feature.overrideName) return feature.overrideName;

  const { feature_type, name, context } = feature;
  const featureTypeLower = feature_type?.toLowerCase();

  switch (featureTypeLower) {
    case 'country':
      return name;

    case 'region':
      return name;

    case 'place':
    case 'locality':
      return name && context?.region
        ? `${name}, ${context.region.region_code ?? context.region.name}`
        : name;

    case 'neighborhood':
      return name && context?.place ? `${name}, ${context.place.name}` : name;

    case 'district':
      return name && context?.region ? `${name}, ${context.region.name}` : name;

    case 'address':
    case 'street':
      return name && context?.place ? `${name}, ${context.place.name}` : name;

    case 'poi':
      return name;

    default:
      // Fallback: preserve current shortPlaceName behavior for unknown types
      return (name || '').split(',')[0]?.trim() || name;
  }
};

/**
 * Format location for medium display (search input, breadcrumb, larger surfaces)
 * Includes "next layer up" for more context than formatPlaceShort
 * Example: "Las Vegas, Nevada" Or "California, United States"
 */
export const formatPlaceMedium = (feature: MapboxFeature | undefined): string | undefined => {
  if (!feature) return undefined;

  if (feature.overrideName) return feature.overrideName;

  const { feature_type, name, context } = feature;
  const featureTypeLower = feature_type?.toLowerCase();

  switch (featureTypeLower) {
    case 'country':
      return name;

    case 'region':
      return name;

    case 'place':
    case 'locality':
      return name && context?.region ? `${name}, ${context?.region.region_code ?? context?.region.name}` : name;

    case 'neighborhood':
      return name && context?.place
        ? `${name}, ${context?.place.name}`
        : name;

    case 'district':
      return name && context?.region ? `${name}, ${context?.region.name}` : name;

    case 'address':
    case 'street':
      return name && context?.place
        ? `${name}, ${context?.place.name}`
        : name;

    case 'poi':
      return name;

    default:
      // Fallback for unknown types
      return (name || '').split(',')[0]?.trim() || name;
  }
};
